import React, { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Plug, CheckCircle, XCircle, ArrowUp, ArrowDown } from "@phosphor-icons/react";

const PROVIDERS = [
  { value: "anthropic", label: "Anthropic" },
  { value: "openai", label: "OpenAI" },
  { value: "gemini", label: "Gemini" },
];

const ALL_PROVIDERS = ["cache", "paapi", "dataforseo", "apify", "direct", "scrapedo", "manual"];
const PROVIDER_DESC = {
  cache:      { label: "Cache",      cost: "Free",          desc: "Look up previously-fetched listings." },
  paapi:      { label: "PA-API",     cost: "Free*",         desc: "Amazon Product Advertising API. Requires Associates approval." },
  dataforseo: { label: "DataForSEO", cost: "~$0.002/ASIN",  desc: "Licensed, reliable, pay-as-you-go." },
  apify:      { label: "Apify",      cost: "Free credits",  desc: "junglee/free-amazon-product-scraper actor." },
  direct:     { label: "Direct",     cost: "Free",          desc: "Multi-strategy scrape from our server." },
  scrapedo:   { label: "scrape.do",  cost: "Per-request",   desc: "Residential proxy + our parser." },
  manual:     { label: "Manual paste", cost: "Free",        desc: "User pastes the listing details." },
};

function Label({ children }) { return <label className="label-mono mb-1.5 block">{children}</label>; }

export default function AdminSettings() {
  const [s, setS] = useState(null);
  const [newKey, setNewKey] = useState("");
  const [creds, setCreds] = useState({});  // write-only fields buffer
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const load = () => api.get("/admin/settings").then((r) => setS(r.data));
  useEffect(() => { load(); }, []);

  if (!s) return <div className="text-ink/60">Loading…</div>;

  const order = s.data_provider_order || ["cache", "apify", "direct", "scrapedo", "manual"];
  const used = new Set(order);
  const available = ALL_PROVIDERS.filter((p) => !used.has(p));

  const setOrder = (next) => setS({ ...s, data_provider_order: next });
  const moveUp = (i) => {
    if (i === 0) return;
    const next = [...order]; [next[i - 1], next[i]] = [next[i], next[i - 1]]; setOrder(next);
  };
  const moveDown = (i) => {
    if (i === order.length - 1) return;
    const next = [...order]; [next[i + 1], next[i]] = [next[i], next[i + 1]]; setOrder(next);
  };
  const removeAt = (i) => setOrder(order.filter((_, idx) => idx !== i));
  const addProvider = (p) => setOrder([...order, p]);

  const save = async () => {
    try {
      const payload = {
        llm_provider: s.llm_provider,
        llm_base_url: s.llm_base_url,
        llm_model: s.llm_model,
        admin_export_email: s.admin_export_email,
        data_provider_order: order,
        cache_ttl_hours: Number(s.cache_ttl_hours) || 24,
        paapi_partner_tag: s.paapi_partner_tag,
        dataforseo_login: s.dataforseo_login,
        ...creds,  // only non-empty write-only fields
      };
      if (newKey.trim()) payload.llm_api_key = newKey.trim();
      await api.put("/admin/settings", payload);
      setNewKey("");
      setCreds({});
      toast.success("Settings saved");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const test = async () => {
    setTesting(true);
    try {
      const { data } = await api.post("/admin/settings/test");
      setTestResult(data);
    } catch (e) { setTestResult({ ok: false, error: apiError(e) }); }
    setTesting(false);
  };

  return (
    <div className="max-w-3xl">
      <div className="border-b border-ink pb-8">
        <div className="label-mono">Admin · № 05</div>
        <h1 className="mt-3 font-display font-black text-4xl md:text-5xl tracking-tightest text-ink leading-[1]" data-testid="admin-settings-title">
          Settings.
        </h1>
        <p className="mt-3 text-ink/65 max-w-xl">
          All analysts call the LLM and Amazon data providers using these values.
          Changes take effect immediately — no redeploy.
        </p>
      </div>

      {/* LLM block */}
      <section className="mt-10 border border-rule bg-white p-6 md:p-8 space-y-6">
        <div className="flex items-center justify-between">
          <div className="font-display text-2xl font-bold tracking-tight">LLM</div>
          {s.is_using_emergent_key && (
            <span className="label-mono bg-cobalt-soft text-cobalt px-2 py-1 border border-cobalt/30">
              Emergent key · default
            </span>
          )}
        </div>
        <div className="grid sm:grid-cols-2 gap-5">
          <div>
            <Label>Provider</Label>
            <Select value={s.llm_provider} onValueChange={(v) => setS({ ...s, llm_provider: v })}>
              <SelectTrigger data-testid="settings-provider" className="rounded-none border-rule h-10"><SelectValue /></SelectTrigger>
              <SelectContent className="rounded-none border-rule">{PROVIDERS.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Model</Label>
            <Input value={s.llm_model || ""} onChange={(e) => setS({ ...s, llm_model: e.target.value })}
              placeholder="e.g. claude-sonnet-4-5-20250929" data-testid="settings-model" />
          </div>
        </div>
        <div>
          <Label>Base URL · optional (openai_compatible)</Label>
          <Input value={s.llm_base_url || ""} onChange={(e) => setS({ ...s, llm_base_url: e.target.value })}
            placeholder="https://api.openai.com/v1" />
        </div>
        <div>
          <Label>API key · write-only</Label>
          <Input type="password" value={newKey} onChange={(e) => setNewKey(e.target.value)}
            placeholder={s.has_api_key ? `Current: ${s.llm_api_key_masked}` : "sk-..."} data-testid="settings-key" />
          <div className="label-mono mt-1.5 text-ink/40">Leave blank to keep the current key.</div>
        </div>
        <Button variant="outline" onClick={test} disabled={testing} data-testid="settings-test">
          <Plug className="w-4 h-4 mr-1.5" /> {testing ? "Testing…" : "Test LLM connection"}
        </Button>
        {testResult && (
          <div className={`px-4 py-3 text-sm flex items-start gap-2.5 border ${testResult.ok ? "border-signal/40 bg-signal-bg text-signal" : "border-alert/40 bg-alert-bg text-alert"}`}>
            {testResult.ok ? <CheckCircle weight="fill" className="w-4 h-4 mt-0.5" /> : <XCircle weight="fill" className="w-4 h-4 mt-0.5" />}
            <div>
              {testResult.ok ? <>
                <strong>Connected.</strong> {testResult.provider} · {testResult.model}
                <div className="label-mono mt-1 text-signal/70">Sample · {testResult.sample}</div>
              </> : <>{testResult.error}</>}
            </div>
          </div>
        )}
      </section>

      {/* Amazon data provider chain */}
      <section className="mt-8 border border-rule bg-white p-6 md:p-8 space-y-6">
        <div>
          <div className="font-display text-2xl font-bold tracking-tight">Amazon data providers</div>
          <p className="text-sm text-ink/65 mt-2 leading-relaxed">
            Catalog Analyzer fetches listings by walking this chain top-to-bottom and stops at the first hit.
            Drag-style reorder with the arrows. Cache + manual are always free; others activate when their credentials are set.
          </p>
        </div>

        <div>
          <Label>Chain order</Label>
          <ol className="border border-rule divide-y divide-rule">
            {order.map((p, i) => {
              const meta = PROVIDER_DESC[p] || { label: p, cost: "", desc: "" };
              const isReady =
                p === "cache" || p === "manual" || p === "direct" ||
                (p === "apify" && s.has_apify_token) ||
                (p === "scrapedo" && s.has_scrapedo_token) ||
                (p === "paapi" && s.has_paapi) ||
                (p === "dataforseo" && s.has_dataforseo);
              return (
                <li key={p} className="flex items-center gap-3 px-4 py-3">
                  <span className="font-mono tabular text-ink/40 w-6">{String(i + 1).padStart(2, "0")}</span>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-ink">{meta.label}</span>
                      <span className="label-mono text-ink/40">{meta.cost}</span>
                      {!isReady && <span className="label-mono text-gold border border-gold/40 px-1.5 py-0.5">needs creds</span>}
                      {isReady && p !== "manual" && p !== "cache" && p !== "direct" && (
                        <span className="label-mono text-signal border border-signal/40 px-1.5 py-0.5">ready</span>
                      )}
                    </div>
                    <div className="text-xs text-ink/55 mt-0.5">{meta.desc}</div>
                  </div>
                  <div className="flex gap-1">
                    <button type="button" onClick={() => moveUp(i)} disabled={i === 0}
                      className="p-1.5 border border-rule hover:bg-surface-hover disabled:opacity-30" data-testid={`chain-up-${p}`}>
                      <ArrowUp className="w-3.5 h-3.5" weight="bold" />
                    </button>
                    <button type="button" onClick={() => moveDown(i)} disabled={i === order.length - 1}
                      className="p-1.5 border border-rule hover:bg-surface-hover disabled:opacity-30" data-testid={`chain-down-${p}`}>
                      <ArrowDown className="w-3.5 h-3.5" weight="bold" />
                    </button>
                    <button type="button" onClick={() => removeAt(i)}
                      className="px-2 py-1.5 border border-rule label-mono text-alert hover:bg-alert-bg" data-testid={`chain-remove-${p}`}>
                      remove
                    </button>
                  </div>
                </li>
              );
            })}
          </ol>
          {available.length > 0 && (
            <div className="mt-3 flex items-center gap-2 flex-wrap">
              <span className="label-mono">Add:</span>
              {available.map((p) => (
                <button key={p} onClick={() => addProvider(p)} data-testid={`chain-add-${p}`}
                  className="label-mono border border-rule px-2 py-1 hover:bg-surface-hover text-ink">
                  + {PROVIDER_DESC[p]?.label || p}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="grid sm:grid-cols-2 gap-5 border-t border-rule pt-6">
          <div>
            <Label>Cache TTL · hours</Label>
            <Input type="number" min={1} max={720}
              value={s.cache_ttl_hours} onChange={(e) => setS({ ...s, cache_ttl_hours: e.target.value })}
              data-testid="settings-cache-ttl" />
          </div>
        </div>

        {/* Credentials — write-only */}
        <div className="border-t border-rule pt-6 space-y-5">
          <div className="label-mono">Provider credentials · write-only</div>
          <CredField label="Apify token" placeholder={s.has_apify_token ? `Current: ${s.apify_token_masked}` : "apify_api_..."}
            value={creds.apify_token || ""} onChange={(v) => setCreds({ ...creds, apify_token: v })} testid="cred-apify" />
          <CredField label="scrape.do token" placeholder={s.has_scrapedo_token ? `Current: ${s.scrapedo_token_masked}` : ""}
            value={creds.scrapedo_token || ""} onChange={(v) => setCreds({ ...creds, scrapedo_token: v })} testid="cred-scrapedo" />
          <div className="grid sm:grid-cols-2 gap-5">
            <CredField label="PA-API access key" placeholder={s.has_paapi ? `Current: ${s.paapi_access_key_masked}` : ""}
              value={creds.paapi_access_key || ""} onChange={(v) => setCreds({ ...creds, paapi_access_key: v })} testid="cred-paapi-access" />
            <CredField label="PA-API secret key" placeholder={s.has_paapi ? `Current: ${s.paapi_secret_key_masked}` : ""}
              value={creds.paapi_secret_key || ""} onChange={(v) => setCreds({ ...creds, paapi_secret_key: v })} testid="cred-paapi-secret" />
          </div>
          <div>
            <Label>PA-API partner tag</Label>
            <Input value={s.paapi_partner_tag || ""} onChange={(e) => setS({ ...s, paapi_partner_tag: e.target.value })}
              placeholder="cliqcommerce-20" data-testid="cred-paapi-tag" />
          </div>
          <div className="grid sm:grid-cols-2 gap-5">
            <div>
              <Label>DataForSEO login</Label>
              <Input value={s.dataforseo_login || ""} onChange={(e) => setS({ ...s, dataforseo_login: e.target.value })}
                placeholder="me@cliqcommerce.com" data-testid="cred-dfs-login" />
            </div>
            <CredField label="DataForSEO password" placeholder={s.has_dataforseo ? `Current: ${s.dataforseo_password_masked}` : ""}
              value={creds.dataforseo_password || ""} onChange={(v) => setCreds({ ...creds, dataforseo_password: v })} testid="cred-dfs-pass" />
          </div>
        </div>
      </section>

      {/* Misc */}
      <section className="mt-8 border border-rule bg-white p-6 md:p-8">
        <Label>Admin export email · EOD CSVs</Label>
        <Input value={s.admin_export_email || ""} onChange={(e) => setS({ ...s, admin_export_email: e.target.value })}
          data-testid="settings-export-email" />
      </section>

      <div className="mt-8 sticky bottom-4">
        <Button onClick={save} className="h-12 px-8 text-base" data-testid="settings-save">Save all settings</Button>
      </div>
    </div>
  );
}

function CredField({ label, placeholder, value, onChange, testid }) {
  return (
    <div>
      <label className="label-mono mb-1.5 block">{label}</label>
      <Input type="password" value={value} onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder || "(unset)"} data-testid={testid} />
      <div className="label-mono mt-1.5 text-ink/40">Leave blank to keep current.</div>
    </div>
  );
}
